import { useTranslations } from 'use-intl';
import { User } from 'lucide-react';
import {
  Avatar,
  Body,
  Button,
  Card,
  Divider,
  DropdownMenu,
  DropdownMenuItem,
  I,
  List,
  Pill,
  Pullquote,
  Row,
  Stack,
} from '@leanin/ui';
import type { MembershipRequestView } from '../../../lib/queries/groups';
import { InlineStatus } from '../../InlineStatus';
import styles from './AdminJoinRequestsCard.module.css';

// The leader join-request list card. Renders the `direction='request'` slice of
// the unified membership-request view for one lifecycle tab (pending / accepted
// / declined). Owns no mutations — the caller passes the decision callbacks and
// the pending flag.
//
// A row is a user applicant (paths A/B; `subject` is the requester) OR a Circle
// applicant (path C; `subjectCircle` + `actingUser`, `subject.userId` null).
// Any row that carries an `intake` snapshot/tombstone gets a "View application"
// action; legacy/flag-off rows (null intake) render exactly as today.

export type ReviewTab = 'pending' | 'accepted' | 'declined';

type Props = {
  readonly requests: ReadonlyArray<MembershipRequestView>;
  readonly status: ReviewTab;
  readonly onApprove: (requestId: string) => void;
  readonly onDecline: (requestId: string) => void;
  readonly onMessage: (userId: string) => void;
  readonly onView: (req: MembershipRequestView) => void;
  /** Navigate to a member applicant's profile. Same handler the review modal uses. */
  readonly onViewProfile: (userId: string) => void;
  /** Navigate to a Circle applicant's page. Same handler the review modal uses. */
  readonly onViewCircle: (circleId: string) => void;
  readonly isPending: boolean;
  /** A failed approve/decline, shown on the row the leader acted on — a
   *  section-level message would sit off-screen for a row below the fold. */
  readonly decisionError: { readonly requestId: string; readonly message: string } | null;
};

function memberSublabel(
  m: { jobTitle: string | null; company: string | null } | null,
): string | null {
  if (m === null) return null;
  return [m.jobTitle, m.company].filter(Boolean).join(' · ') || null;
}

function formatRelative(
  iso: string,
  t: ReturnType<typeof useTranslations<'Group'>>,
): string {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return '';
  const diffMs = Date.now() - then;
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return t('admin.requests.relativeJustNow');
  if (mins < 60) return t('admin.requests.relativeMinutes', { count: mins });
  const hours = Math.round(mins / 60);
  if (hours < 24) return t('admin.requests.relativeHours', { count: hours });
  const days = Math.round(hours / 24);
  if (days < 7) return t('admin.requests.relativeDays', { count: days });
  const weeks = Math.round(days / 7);
  if (weeks < 5) return t('admin.requests.relativeWeeks', { count: weeks });
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

export function AdminJoinRequestsCard({
  requests,
  status,
  onApprove,
  onDecline,
  onMessage,
  onView,
  onViewProfile,
  onViewCircle,
  isPending,
  decisionError,
}: Props) {
  const t = useTranslations('Group');
  if (requests.length === 0) {
    const emptyKey =
      status === 'accepted'
        ? 'admin.requests.emptyAccepted'
        : status === 'declined'
          ? 'admin.requests.emptyDeclined'
          : 'admin.requests.empty';
    return (
      <Card pad="flush">
        <Row p="xl" justify="center">
          <Body size="sm" color="mute">
            {t(emptyKey)}
          </Body>
        </Row>
      </Card>
    );
  }

  return (
    <Card pad="flush">
      <List bordered={false}>
        {requests.map((req) => {
          const isCircle = req.applicantType === 'circle';
          const subject = req.subject;
          const circle = req.subjectCircle ?? null;
          const acting = req.actingUser ?? null;
          const userName =
            `${subject.firstName ?? ''} ${subject.lastName ?? ''}`.trim() ||
            t('admin.requests.unknownName');
          const actingName =
            `${acting?.firstName ?? ''} ${acting?.lastName ?? ''}`.trim();
          const displayName = isCircle
            ? (circle?.name ?? t('admin.requests.unknownName'))
            : userName;
          // Message target: a member applicant DMs the requester; a Circle
          // applicant DMs the Circle's acting leader (who submitted on the
          // Circle's behalf — the Circle itself has no user to DM). Null when
          // there is no messageable person, in which case the button is hidden.
          const messageUserId = isCircle
            ? (acting?.userId ?? null)
            : (subject.userId ?? null);
          const messageName = isCircle ? actingName : displayName;
          const hasIntake = req.intake != null;
          const rowError =
            decisionError !== null && decisionError.requestId === req.id ? (
              <InlineStatus>{decisionError.message}</InlineStatus>
            ) : null;
          // Entity-aware "view" action, mirroring the review modal: a member
          // applicant → their profile (/members/$id); a Circle applicant →
          // the Circle page (/circles/$id). Null when the target id is absent.
          const viewAction = isCircle
            ? circle?.id != null
              ? {
                  label: t('admin.viewApplication.viewCircle'),
                  icon: I.users,
                  onClick: () => onViewCircle(circle.id),
                }
              : null
            : subject.userId != null
              ? {
                  label: t('admin.viewApplication.viewProfile'),
                  icon: <User size={16} strokeWidth={1.75} aria-hidden />,
                  onClick: () => onViewProfile(subject.userId as string),
                }
              : null;

          // Declined rows use a compact single-row layout: identity on the
          // left, and a right-aligned [Declined pill · Approve · ⋯] cluster.
          // Secondary actions (view application/profile/circle, message) move
          // into the overflow menu, so there's no standalone view button or
          // vertical divider here.
          if (status === 'declined') {
            return (
              <Stack key={req.id} gap="sm" p="xl">
                <Row
                  gap="lg"
                  align="center"
                  justify="space-between"
                  wrap
                >
                  <Row gap="md" align="center" style={{ flex: '1 1 auto', minWidth: 0 }}>
                    <Avatar
                      {...(!isCircle && subject.photoUrl ? { src: subject.photoUrl } : {})}
                      name={displayName}
                      size={44}
                    />
                    <Stack gap="xs" style={{ minWidth: 0 }}>
                      <Row gap="sm" align="center" wrap>
                        <Body size="md">{displayName}</Body>
                        {isCircle ? (
                          <Pill tone="navy" icon={I.circles}>
                            {t('admin.requests.circleApplicant')}
                          </Pill>
                        ) : null}
                      </Row>
                      <Body size="sm" color="mute">
                        {t('admin.requests.declinedAt', {
                          time: formatRelative(req.decidedAt ?? req.createdAt, t),
                        })}
                      </Body>
                    </Stack>
                  </Row>

                  <Row gap="sm" align="center" wrap>
                    <Button
                      variant="softDanger"
                      size="md"
                      icon={I.check}
                      onClick={() => onApprove(req.id)}
                      disabled={isPending}
                    >
                      {t('admin.requests.approve')}
                    </Button>
                    <DropdownMenu
                      align="end"
                      trigger={
                        <Button
                          variant="ghost"
                          size="md"
                          icon={I.more}
                          aria-label={t('admin.requests.moreActions', { name: displayName })}
                        />
                      }
                    >
                      {hasIntake ? (
                        <DropdownMenuItem onSelect={() => onView(req)}>
                          {t('admin.viewApplication.viewButton')}
                        </DropdownMenuItem>
                      ) : null}
                      {viewAction ? (
                        <DropdownMenuItem onSelect={viewAction.onClick}>
                          {viewAction.label}
                        </DropdownMenuItem>
                      ) : null}
                      {messageUserId ? (
                        <DropdownMenuItem onSelect={() => onMessage(messageUserId)}>
                          {t('admin.requests.messageAction')}
                        </DropdownMenuItem>
                      ) : null}
                    </DropdownMenu>
                  </Row>
                </Row>
                {rowError}
              </Stack>
            );
          }

          return (
            <Row key={req.id} gap="lg" align="center" wrap p="xl">
              <Row
                gap="md"
                align="flex-start"
                style={{ flex: '2 1 360px', minWidth: 0 }}
              >
                <Avatar
                  {...(!isCircle && subject.photoUrl ? { src: subject.photoUrl } : {})}
                  name={displayName}
                  size={44}
                />
                <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
                  <Stack gap="xs">
                    <Row gap="sm" align="center" wrap>
                      <Body size="md">{displayName}</Body>
                      {isCircle ? (
                        <Pill tone="navy" icon={I.circles}>
                          {t('admin.requests.circleApplicant')}
                        </Pill>
                      ) : null}
                    </Row>
                    {status === 'accepted' ? (
                      <Body size="sm" color="mute">
                        {t('admin.requests.acceptedAt', {
                          time: formatRelative(req.decidedAt ?? req.createdAt, t),
                        })}
                      </Body>
                    ) : (
                      <Row gap="xs" align="center" wrap>
                        <Body size="sm" color="mute">
                          {isCircle
                            ? actingName.length > 0
                              ? t('admin.requests.viaLeader', { name: actingName })
                              : t('admin.requests.circleFallback')
                            : (memberSublabel(subject) ??
                              t('admin.requests.memberFallback'))}
                        </Body>
                        <Body size="sm" color="mute">
                          · {t('admin.requests.requested', {
                            time: formatRelative(req.createdAt, t),
                          })}
                        </Body>
                      </Row>
                    )}
                  </Stack>
                  {!isCircle && req.message !== null && req.message.length > 0 ? (
                    <Pullquote tone="neutral">{req.message}</Pullquote>
                  ) : null}
                  {hasIntake ? (
                    <Row>
                      <Pill
                        tone="neutral"
                        size="sm"
                        onClick={() => onView(req)}
                      >
                        {t('admin.viewApplication.viewButton')}
                      </Pill>
                    </Row>
                  ) : null}
                </Stack>
              </Row>

              <Divider vertical className={styles.cardDivider} />

              <Stack
                gap="sm"
                ps="md"
                style={{ flex: '1 1 200px', minWidth: 200 }}
              >
                {status === 'pending' ? (
                  <>
                    <Row gap="sm">
                      <Button
                        variant="primary"
                        size="sm"
                        icon={I.check}
                        style={{ flex: 1, justifyContent: 'center' }}
                        onClick={() => onApprove(req.id)}
                        disabled={isPending}
                      >
                        {t('admin.requests.approve')}
                      </Button>
                      <Button
                        variant="ghostDanger"
                        size="sm"
                        style={{ flex: 1, justifyContent: 'center' }}
                        onClick={() => onDecline(req.id)}
                        disabled={isPending}
                      >
                        {t('admin.requests.decline')}
                      </Button>
                    </Row>
                    {messageUserId ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={I.mail}
                        block
                        onClick={() => onMessage(messageUserId)}
                        disabled={isPending}
                      >
                        {t('admin.requests.message', {
                          name: messageName.split(' ')[0] ?? '',
                        })}
                      </Button>
                    ) : null}
                    {rowError}
                  </>
                ) : (
                  <div className={styles.acceptedActions}>
                    {viewAction ? (
                      <Button
                        variant="secondary"
                        size="md"
                        icon={viewAction.icon}
                        block
                        onClick={viewAction.onClick}
                      >
                        {viewAction.label}
                      </Button>
                    ) : null}
                    {messageUserId ? (
                      <Button
                        variant="secondary"
                        size="md"
                        icon={I.mail}
                        block
                        onClick={() => onMessage(messageUserId)}
                      >
                        {t('admin.requests.messageAction')}
                      </Button>
                    ) : null}
                  </div>
                )}
              </Stack>
            </Row>
          );
        })}
      </List>
    </Card>
  );
}
